<script setup lang="ts">
import { computed } from "vue";
import { AwSkeleton } from "@asap/design";
import { statusColorName, statusGradientClass, UNAVAILABLE_KPI_LABEL, type ChartStatus } from "./theme";

const props = withDefaults(
  defineProps<{
    loading?: boolean;
    label?: string;
    value?: string;
    delta?: string;
    /** Colors the delta by direction of change, independently of the achievement status. */
    deltaStatus?: ChartStatus;
    comparison?: string;
    progressPercent?: number;
    target?: string;
    status?: ChartStatus;
    /** "tinted" color the tile background according to the status, like the KPI of the Trajectory. */
    variant?: "tinted" | "plain";
    /** Greys the tile out and replaces the value by UNAVAILABLE_KPI_LABEL (KPI sourced from PLD). */
    unavailable?: boolean;
    /** API returned null — shows MISSING_KPI_VALUE and hides the progress bar. */
    missing?: boolean;
  }>(),
  {
    loading: false,
    status: "success",
    variant: "tinted",
    delta: undefined,
    deltaStatus: undefined,
    label: undefined,
    value: undefined,
    progressPercent: undefined,
    target: undefined,
    comparison: undefined,
    unavailable: false,
    missing: false,
  }
);

const colorName = computed(() => statusColorName(props.status));

const deltaColorName = computed(() => statusColorName(props.deltaStatus ?? props.status));

const isTintedValue = computed(
  () => !props.loading && !props.unavailable && !props.missing && props.variant === "tinted"
);

const containerClass = computed(() => {
  const compact = ["rounded-lg", "px-2.5", "pb-2", "pt-2.5"];
  const roomy = ["rounded-xl", "p-3"];

  if (props.loading) return ["bg-white", "border-secondary-200", ...compact];
  if (props.unavailable) return ["bg-secondary-50", "border-dashed", "border-secondary-200", ...compact];

  const surface =
    props.variant === "tinted"
      ? [statusGradientClass(props.status), `border-${colorName.value}-100`]
      : ["bg-white", "border-secondary-200"];

  return [...surface, ...(isTintedValue.value ? roomy : compact)];
});

const ui = {
  container: "flex flex-col gap-2 overflow-hidden border",
  label: "text-md text-secondary-700",
  valueRow: "flex flex-wrap items-baseline gap-1.5",
  value: "text-2xl font-semibold leading-none text-secondary-900",
  unavailableValue: "text-sm font-medium text-secondary-700",
  delta: "text-xs font-medium",
  comparison: "text-xs text-secondary-700",
  skeletonLabel: "h-4 w-24",
  skeletonValue: "h-8 w-14",
  progress: "mt-1",
  skeletonBar: "mt-1 h-0.5 w-full rounded-full",
  skeletonBarLabels: "flex items-center justify-between gap-2",
  skeletonBarLabel: "h-3 w-8",
  skeletonBarTarget: "h-3 w-12",
};
</script>

<template>
  <div :class="[ui.container, containerClass]">
    <template v-if="loading">
      <AwSkeleton :class="ui.skeletonLabel" />
      <AwSkeleton :class="ui.skeletonValue" />
      <AwSkeleton :class="ui.skeletonBar" />
      <div :class="ui.skeletonBarLabels">
        <AwSkeleton :class="ui.skeletonBarLabel" />
        <AwSkeleton :class="ui.skeletonBarTarget" />
      </div>
    </template>

    <template v-else>
      <span :class="ui.label">{{ label }}</span>

      <template v-if="unavailable">
        <span :class="ui.unavailableValue">{{ UNAVAILABLE_KPI_LABEL }}</span>
      </template>

      <template v-else>
        <div :class="ui.valueRow">
          <span :class="ui.value">{{ value }}</span>
          <span v-if="delta" :class="[ui.delta, `text-${deltaColorName}-600`]">{{ delta }}</span>
          <span v-if="comparison" :class="ui.comparison">{{ comparison }}</span>
        </div>

        <ConsultantKpiProgressBar
          v-if="!missing && progressPercent !== undefined"
          :class="ui.progress"
          :percent="progressPercent"
          :status="status"
          :tinted-track="variant === 'tinted'"
          :leading-label="`${progressPercent}%`"
          :trailing-label="target"
        />
      </template>
    </template>
  </div>
</template>
